import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Button, Flex, Icon, Link, Text } from '@pega/cosmos-react-core';
import CustomTooltip from '../../../../Utilities/CustomTooltip';
import { appState } from '../../../../../interfaces/common';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../../redux/actions';
import {
  getCustomFileUploadMetaData,
  setUpdateCustomTranslation,
  validateUploadLanguageTemplate,
} from '../../ChatApis';
import useLocalTranslation from '../../../../../hooks/useLocalTranslation';
import * as XLSX from 'xlsx';
import { languageMappingForTranslation } from '../../../../../constants/languageCodes';

const StyledChooseFileButton = styled.button(
  () => css`
    color: #076bc9;
    margin-left: 5px;
  `,
);

const StyledExitButton = styled.button(
  () => css`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
  `,
);

const StyledFlexItem = styled.div(
  () => css`
    background: #d9e8f6;
    border: 0 solid transparent;
    border-radius: calc(1.125 * 0.5rem);
    color: #000000;
    cursor: default;
    display: inline-flex;
    align-items: center;
    font-size: 0.75rem;
    height: 1.125rem;
    padding: 0 0.5rem;
    min-width: max-content;
    line-height: 1;
  `,
);

const StyledContainer = styled.div(
  () => css`
    color: #000000;
    background-color: #ffffff;
    border-radius: calc(0.5rem * 0);
    border-color: #939393;
    border-width: 0.0625rem;
    border-style: solid;
    width: 50%;
  `,
);

const CustomLocalization = () => {
  const {
    sessionId,
    chatInfo: {
      connectionId,
      customTranslationLanguageSet,
      localizationFileName,
      localizationFileUrl,
      languageTemplateUrl,
      languageCodeList,
    },
  } = useSelector((state: appState) => state);

  const fileRef = useRef<any>(null);

  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();
  const trashIconClickHandler = async (key: any) => {
    dispatch(updateLoaderDetails(true));
    const data: any = { ...customTranslationLanguageSet };
    delete data[key];
    const updatedLanguageCodeList = languageCodeList.filter((languageCode: string) => {
      return languageCode !== key;
    });
    let response;
    try {
      response = await setUpdateCustomTranslation(sessionId, connectionId, {
        customTranslationLanguageSet: data,
        languageCodeList: updatedLanguageCodeList,
      });
    } catch (error) {
      dispatch(updateBannerInfo(translate('language_code_removal_failed'), 'success'));
    }
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      dispatch(setChatInfo(response?.data));
      dispatch(updateBannerInfo(translate('language_code_removal_succeeded'), 'success'));
    } else {
      dispatch(updateBannerInfo(translate('language_code_removal_failed'), 'success'));
    }
  };

  const renderLanguageCodes = () => {
    return (
      <Flex container={{ pad: 1, gap: 1, justify: 'start', wrap: 'wrap' }} as={StyledContainer}>
        {languageCodeList?.map((data: any) => {
          return (
            <StyledFlexItem key={data}>
              <Text variant="secondary">{languageMappingForTranslation[data]}</Text>
              <StyledExitButton onClick={() => trashIconClickHandler(data)}>
                <Icon name="times" style={{ width: '0.8rem', height: '0.8rem', marginInlineStart: '0.5rem' }}></Icon>
              </StyledExitButton>
            </StyledFlexItem>
          );
        })}
      </Flex>
    );
  };

  const processExcel = async (data: any) => {
    const workbook = XLSX.read(data, { type: 'binary' });
    const firstSheet = workbook.SheetNames[0];
    const excelRows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet]);
    let languagesPresentInExcelSheet: any = [];

    const customizedLanguageSet: any = excelRows.reduce((languageGroup: any, row: any) => {
      const rowKeys = Object.keys(row);
      const rowValues = Object.values(row);
      const label: any = rowValues[0];
      rowKeys.forEach((element, index) => {
        if (index !== 0) {
          const language = element;
          if (languageGroup[language]) {
            languageGroup[language][label] = row[element];
          } else {
            languageGroup[language] = {};
            languagesPresentInExcelSheet.push(language);
            languageGroup[language][label] = row[element];
          }
        }
      });
      return languageGroup;
    }, {});

    const sortedLanguageCodeList = languagesPresentInExcelSheet.sort();
    const dataToSend: any = {
      customTranslationLanguageSet: customizedLanguageSet,
      languageCodeList: sortedLanguageCodeList,
    };
    await setUpdateCustomTranslation(sessionId, connectionId, dataToSend);
    return;
  };

  const validateFile = async (requestId: string, fileNameWithoutExtension: string) => {
    let response;
    try {
      response = await validateUploadLanguageTemplate(sessionId, connectionId, requestId, fileNameWithoutExtension);
    } catch (error) {
      dispatch(updateBannerInfo(translate('language_template_upload_failed'), 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      dispatch(setChatInfo(response.data));
      dispatch(updateBannerInfo(translate('language_template_upload_success'), 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('language_template_upload_failed'), 'urgent'));
    }
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateLoaderDetails(true));
    const file = event?.currentTarget?.files?.[0];
    const fileType = file?.name?.split('.')?.pop() as string;
    if (fileType === 'csv') {
      if (typeof FileReader !== 'undefined') {
        const reader = new FileReader();
        if (reader.readAsBinaryString) {
          reader.onload = () => {
            processExcel(reader.result);
          };
          file && reader.readAsBinaryString(file);
        }
      } else {
        console.log('This browser does not support HTML5.');
      }
      const fileName = file?.name;
      const fileNameWithoutExtension = fileName?.split('.')[0] || '';
      const response = await getCustomFileUploadMetaData(sessionId, connectionId, fileNameWithoutExtension);
      const data = response?.data;
      uploadFile(data, file, fileNameWithoutExtension);
    } else {
      dispatch(updateLoaderDetails(false));
      dispatch(updateBannerInfo(translate('must_have_csv_extension'), 'urgent'));
    }
  };

  const uploadFile = async (urlAndFields: any, file: any, fileNameWithoutExtension: string) => {
    const { url, fields } = urlAndFields;
    const requestId = fields['x-amz-meta-request-id'];
    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append('file', file);
    await fetch(url, {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        if (requestId) {
          validateFile(requestId, fileNameWithoutExtension);
        }
      })
      .catch(() => {
        dispatch(updateLoaderDetails(false));
        dispatch(updateBannerInfo(translate('language_template_upload_failed'), 'urgent'));
      });
  };

  return (
    <Flex container={{ direction: 'column', itemGap: 1.5 }}>
      <Text variant="h3">
        {translate('system_field_localization')}
        <CustomTooltip
          info={translate('system_field_localization_tooltip')}
          tooltipStyle={{ marginLeft: '5px' }}
          iconName="help"
        />
      </Text>
      <Text>
        {translate('custom_field_localization_template')}
        <Link style={{ marginLeft: 5 }} href={languageTemplateUrl || ''} target="_blank">
          {translate('download')}
        </Link>
        <CustomTooltip
          info={translate('custom_field_localization_temp_tooltip')}
          tooltipStyle={{ marginLeft: '5px' }}
          iconName="help"
        />
      </Text>
      <Flex container={{ direction: 'row' }}>
        <Text>
          {translate('upload_custom_localization')}
          <Button
            className="pointer"
            onClick={() => {
              fileRef?.current.click();
            }}
            as={StyledChooseFileButton}
            variant="link"
          >
            {translate('choose_file')}
          </Button>
        </Text>

        <input type="file" accept=".csv" ref={fileRef} onChange={onFileChange} style={{ display: 'none' }} />
      </Flex>
      <Flex>
        {localizationFileUrl && localizationFileName && (
          <Link href={localizationFileUrl} target="_blank">
            {localizationFileName}
            <Icon name="download" />
          </Link>
        )}
      </Flex>
      {localizationFileName && renderLanguageCodes()}
    </Flex>
  );
};

export default CustomLocalization;
